import React, { useCallback, useEffect, useState } from "react"

import { format } from "date-fns"
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import ApplicantDetailsMain from "@/components/applicants/ApplicantDetailsMain"
import DocumentVerification from "@/components/applicants/DocumentVerification"
import EditApplicantDialog from "@/components/applicants/EditApplicantDialog"
import ScheduleDialog from "@/components/applicants/ScheduleDialog"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { useApplicationDetails } from "@/hooks/useApplicationDetails"
import { supabase } from "@/lib/supabase"

import type { Tables } from "../../database.types"
import DashboardLayout from "../layouts/DashboardLayout"

const ApplicationCompare: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Get application IDs from URL params
  const appAFromUrl = searchParams.get("appA") || ""
  const appBFromUrl = searchParams.get("appB") || ""

  // Input states
  const [idAInput, setIdAInput] = useState<string>(appAFromUrl)
  const [idBInput, setIdBInput] = useState<string>(appBFromUrl)
  const [idAFetch, setIdAFetch] = useState<string>("")
  const [idBFetch, setIdBFetch] = useState<string>("")
  const [resolving, setResolving] = useState<boolean>(false)
  const [resolveError, setResolveError] = useState<string | null>(null)

  // Fetch application details for both
  const {
    applicationDetails: appA,
    isLoading: loadingA,
    isError: isErrorA,
    error: errorA,
  } = useApplicationDetails(idAFetch || undefined)

  const {
    applicationDetails: appB,
    isLoading: loadingB,
    isError: isErrorB,
    error: errorB,
  } = useApplicationDetails(idBFetch || undefined)

  const [isEditDialogOpenA, setIsEditDialogOpenA] = useState<boolean>(false)
  const [isDocumentDialogOpenA, setIsDocumentDialogOpenA] = useState<boolean>(false)
  const [isScheduleDialogOpenA, setIsScheduleDialogOpenA] = useState<boolean>(false)
  const [sessionTypeA, setSessionTypeA] = useState<"orientation" | "vehicle_collection">(
    "orientation",
  )
  const [isReschedulingA, setIsReschedulingA] = useState<boolean>(false)
  const [selectedDocumentA, setSelectedDocumentA] = useState<Tables<"documents"> | null>(null)

  const [isEditDialogOpenB, setIsEditDialogOpenB] = useState<boolean>(false)
  const [isDocumentDialogOpenB, setIsDocumentDialogOpenB] = useState<boolean>(false)
  const [isScheduleDialogOpenB, setIsScheduleDialogOpenB] = useState<boolean>(false)
  const [sessionTypeB, setSessionTypeB] = useState<"orientation" | "vehicle_collection">(
    "orientation",
  )
  const [isReschedulingB, setIsReschedulingB] = useState<boolean>(false)
  const [selectedDocumentB, setSelectedDocumentB] = useState<Tables<"documents"> | null>(null)

  // Suggestions state
  const [suggestions, setSuggestions] = useState<
    Array<{
      application_id: string
      id: string
      current_stage: string | null
      status: string | null
      created_at: string | null
      applicant_first_name: string | null
      applicant_last_name: string | null
    }>
  >([])
  const [loadingSuggestions, setLoadingSuggestions] = useState<boolean>(false)

  // Resolve application_id to UUID
  const resolveByApplicationId = useCallback(
    async (applicationIdValue: string): Promise<string | null> => {
      if (!applicationIdValue.trim()) return null

      const { data, error } = await supabase
        .from("applications")
        .select("id")
        .eq("application_id", applicationIdValue.trim())
        .single()

      if (error || !data) {
        throw new Error(`Application with application_id "${applicationIdValue}" not found`)
      }

      return data.id
    },
    [],
  )

  // Auto-fetch appA when URL param is present (even without appB)
  useEffect(() => {
    if (appAFromUrl && !idAFetch && !resolving) {
      const autoFetchA = async () => {
        setResolveError(null)
        setResolving(true)

        try {
          const uuidA = await resolveByApplicationId(appAFromUrl)
          if (!uuidA) {
            throw new Error("Failed to resolve application ID")
          }
          setIdAFetch(uuidA)
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to resolve application ID"
          setResolveError(errorMessage)
          toast.error("Error", { description: errorMessage })
        } finally {
          setResolving(false)
        }
      }

      autoFetchA()
    }
  }, [appAFromUrl, idAFetch, resolving, resolveByApplicationId])

  // Auto-fetch appB when URL param is present
  useEffect(() => {
    if (appBFromUrl && !idBFetch && !resolving) {
      const autoFetchB = async () => {
        setResolveError(null)
        setResolving(true)

        try {
          const uuidB = await resolveByApplicationId(appBFromUrl)
          if (!uuidB) {
            throw new Error("Failed to resolve application ID")
          }
          setIdBFetch(uuidB)
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to resolve application ID"
          setResolveError(errorMessage)
          toast.error("Error", { description: errorMessage })
        } finally {
          setResolving(false)
        }
      }

      autoFetchB()
    }
  }, [appBFromUrl, idBFetch, resolving, resolveByApplicationId])

  // Update inputs when URL params change
  useEffect(() => {
    if (appAFromUrl) {
      setIdAInput(appAFromUrl)
    }
    if (appBFromUrl) {
      setIdBInput(appBFromUrl)
    }
  }, [appAFromUrl, appBFromUrl])

  // Fetch suggestions based on email when appA is loaded
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!appA?.applicant?.email || !appA?.id) {
        setSuggestions([])
        return
      }

      setLoadingSuggestions(true)
      try {
        // Fetch all applications where the applicant's email matches
        // We need to join through the applicant table to compare emails
        const { data, error } = await supabase
          .from("applications")
          .select(
            `
            id, 
            application_id, 
            current_stage, 
            status,
            created_at,
            applicant:applicant_id!inner(
              email,
              first_name,
              last_name
            )
          `,
          )
          .neq("id", appA.id) // Exclude the current application
          .neq("current_stage", "rejected") // Exclude rejected applications
          .eq("applicant.email", appA.applicant?.email ?? "") // Match by applicant email
          .order("created_at", { ascending: false })
          .limit(10)

        if (error) {
          console.error("Error fetching suggestions:", error)
          setSuggestions([])
        } else {
          // Filter out any null results and map to our expected format
          const validSuggestions = (data || [])
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter((item: any) => item.application_id)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((item: any) => ({
              id: item.id,
              application_id: item.application_id,
              current_stage: item.current_stage,
              status: item.status,
              created_at: item.created_at,
              applicant_first_name: item.applicant?.first_name || null,
              applicant_last_name: item.applicant?.last_name || null,
            }))
          setSuggestions(validSuggestions)
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error)
        setSuggestions([])
      } finally {
        setLoadingSuggestions(false)
      }
    }

    fetchSuggestions()
  }, [appA?.applicant?.email, appA?.id])

  const handleSuggestionClick = async (applicationId: string) => {
    try {
      const uuid = await resolveByApplicationId(applicationId)
      if (uuid) {
        setIdBFetch(uuid)
        setIdBInput(applicationId)
        // Update URL
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set("appB", applicationId)
        navigate(`/applications/compare?${newSearchParams.toString()}`, { replace: true })
      }
    } catch (error) {
      toast.error("Error", {
        description: error instanceof Error ? error.message : "Failed to load application",
      })
    }
  }

  const handleFetch = async () => {
    if (!idAInput.trim() || !idBInput.trim()) {
      toast.error("Validation Error", { description: "Please enter both application IDs" })
      return
    }

    setResolveError(null)
    setResolving(true)

    try {
      const uuidA = await resolveByApplicationId(idAInput)
      const uuidB = await resolveByApplicationId(idBInput)

      if (!uuidA || !uuidB) {
        throw new Error("Failed to resolve one or both application IDs")
      }

      setIdAFetch(uuidA)
      setIdBFetch(uuidB)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to resolve application IDs"
      setResolveError(errorMessage)
      toast.error("Error", { description: errorMessage })
    } finally {
      setResolving(false)
    }
  }

  // Handler functions for Application A
  const handleViewDocumentA = (document: Tables<"documents">) => {
    setSelectedDocumentA(document)
    setIsDocumentDialogOpenA(true)
  }

  const handleRescheduleOrientationA = (_bookingId: string) => {
    setSessionTypeA("orientation")
    setIsReschedulingA(true)
    setIsScheduleDialogOpenA(true)
  }

  const handleRescheduleVehicleCollectionA = (_bookingId: string) => {
    setSessionTypeA("vehicle_collection")
    setIsReschedulingA(true)
    setIsScheduleDialogOpenA(true)
  }

  const handleScheduleDialogCloseA = () => {
    setIsScheduleDialogOpenA(false)
    setIsReschedulingA(false)
  }

  // Handler functions for Application B
  const handleViewDocumentB = (document: Tables<"documents">) => {
    setSelectedDocumentB(document)
    setIsDocumentDialogOpenB(true)
  }

  const handleRescheduleOrientationB = (_bookingId: string) => {
    setSessionTypeB("orientation")
    setIsReschedulingB(true)
    setIsScheduleDialogOpenB(true)
  }

  const handleRescheduleVehicleCollectionB = (_bookingId: string) => {
    setSessionTypeB("vehicle_collection")
    setIsReschedulingB(true)
    setIsScheduleDialogOpenB(true)
  }

  const handleScheduleDialogCloseB = () => {
    setIsScheduleDialogOpenB(false)
    setIsReschedulingB(false)
  }

  const isLoading = loadingA || loadingB || resolving
  const anyError = isErrorA || isErrorB || !!resolveError
  const errorMsg =
    resolveError ||
    (errorA as Error)?.message ||
    (errorB as Error)?.message ||
    "Failed to load applications"

  return (
    <DashboardLayout>
      <div className="flex items-center mb-6">
        <Button variant="ghost" onClick={() => navigate("/applications")} className="mr-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Applications
        </Button>
        <h1 className="text-2xl font-bold">Compare Applications</h1>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Enter Two Application IDs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="col-span-1">
              <label className="text-sm text-muted-foreground">Application A application_id</label>
              <Input
                placeholder="Enter application A application_id"
                value={idAInput}
                onChange={(e) => setIdAInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleFetch()
                  }
                }}
              />
            </div>
            <div className="col-span-1">
              <label className="text-sm text-muted-foreground">Application B application_id</label>
              <Input
                placeholder="Enter application B application_id"
                value={idBInput}
                onChange={(e) => setIdBInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleFetch()
                  }
                }}
              />
            </div>
            <div className="col-span-1">
              <Button
                onClick={handleFetch}
                disabled={isLoading || resolving}
                className="w-full md:w-auto"
              >
                {isLoading || resolving ? "Loading..." : "Compare"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {anyError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}

      {/* Always show both columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Application A */}
        <div className="flex flex-col">
          <Card className="mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-center">Application A</CardTitle>
              <p className="text-center text-sm text-muted-foreground mt-2">
                {appA?.application_id || "-"}
              </p>
            </CardHeader>
          </Card>
          {(loadingA || resolving) && !appA ? (
            <div>
              <Skeleton className="h-8 w-1/3 mb-4" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : appA ? (
            <ApplicantDetailsMain
              application={{
                ...appA,
                documents: appA.documents || [],
              }}
              onViewDocument={handleViewDocumentA}
              onEditApplicant={() => setIsEditDialogOpenA(true)}
              onScheduleOrientation={() => {
                setSessionTypeA("orientation")
                setIsReschedulingA(false)
                setIsScheduleDialogOpenA(true)
              }}
              onScheduleVehicleCollection={() => {
                setSessionTypeA("vehicle_collection")
                setIsReschedulingA(false)
                setIsScheduleDialogOpenA(true)
              }}
              onRescheduleOrientation={handleRescheduleOrientationA}
              onRescheduleVehicleCollection={handleRescheduleVehicleCollectionA}
            />
          ) : (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">-</p>
            </div>
          )}
        </div>

        {/* Application B */}
        <div className="flex flex-col">
          <Card className="mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-center">Application B</CardTitle>
              <p className="text-center text-sm text-muted-foreground mt-2">
                {appB?.application_id || "-"}
              </p>
            </CardHeader>
          </Card>
          {(loadingB || resolving) && !appB ? (
            <div>
              <Skeleton className="h-8 w-1/3 mb-4" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : appB ? (
            <ApplicantDetailsMain
              application={{
                ...appB,
                documents: appB.documents || [],
              }}
              onViewDocument={handleViewDocumentB}
              onEditApplicant={() => setIsEditDialogOpenB(true)}
              onScheduleOrientation={() => {
                setSessionTypeB("orientation")
                setIsReschedulingB(false)
                setIsScheduleDialogOpenB(true)
              }}
              onScheduleVehicleCollection={() => {
                setSessionTypeB("vehicle_collection")
                setIsReschedulingB(false)
                setIsScheduleDialogOpenB(true)
              }}
              onRescheduleOrientation={handleRescheduleOrientationB}
              onRescheduleVehicleCollection={handleRescheduleVehicleCollectionB}
            />
          ) : (
            <div className="flex flex-col">
              {appA && (
                <Card className="mb-4">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Suggested Applications</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Other applications from the same email address
                    </p>
                  </CardHeader>
                  <CardContent>
                    {loadingSuggestions ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    ) : suggestions.length > 0 ? (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {suggestions.map((suggestion) => (
                          <Card
                            key={suggestion.id}
                            className="cursor-pointer hover:bg-accent transition-colors"
                            onClick={() => handleSuggestionClick(suggestion.application_id)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-bold text-sm">
                                      {suggestion.application_id}
                                    </span>
                                    {suggestion.status && (
                                      <Badge
                                        variant={
                                          suggestion.status === "active"
                                            ? "default"
                                            : suggestion.status === "withdrawn"
                                              ? "destructive"
                                              : suggestion.status === "rejected"
                                                ? "destructive"
                                                : "secondary"
                                        }
                                        className="text-xs"
                                      >
                                        {suggestion.status}
                                      </Badge>
                                    )}
                                  </div>
                                  {(suggestion.applicant_first_name ||
                                    suggestion.applicant_last_name) && (
                                    <p className="text-xs text-muted-foreground mb-1">
                                      {[
                                        suggestion.applicant_first_name,
                                        suggestion.applicant_last_name,
                                      ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    </p>
                                  )}
                                  {suggestion.created_at && (
                                    <p className="text-xs text-muted-foreground">
                                      {format(new Date(suggestion.created_at), "MMM d, yyyy")}
                                    </p>
                                  )}
                                </div>
                                <div className="flex-shrink-0">
                                  {suggestion.current_stage && (
                                    <Badge variant="outline" className="text-xs whitespace-nowrap">
                                      {suggestion.current_stage}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No other applications found
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">-</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs for Application A */}
      {appA && (
        <>
          <EditApplicantDialog
            isOpen={isEditDialogOpenA}
            onClose={() => setIsEditDialogOpenA(false)}
            application={appA}
          />

          {selectedDocumentA && (
            <DocumentVerification
              isOpen={isDocumentDialogOpenA}
              onClose={() => setIsDocumentDialogOpenA(false)}
              document={selectedDocumentA}
            />
          )}

          <ScheduleDialog
            isOpen={isScheduleDialogOpenA}
            onClose={handleScheduleDialogCloseA}
            applicationUUID={appA?.id}
            applicationId={appA?.application_id || ""}
            jobId={appA?.job?.id || ""}
            sessionType={sessionTypeA}
            isRescheduling={isReschedulingA}
          />
        </>
      )}

      {/* Dialogs for Application B */}
      {appB && (
        <>
          <EditApplicantDialog
            isOpen={isEditDialogOpenB}
            onClose={() => setIsEditDialogOpenB(false)}
            application={appB}
          />

          {selectedDocumentB && (
            <DocumentVerification
              isOpen={isDocumentDialogOpenB}
              onClose={() => setIsDocumentDialogOpenB(false)}
              document={selectedDocumentB}
            />
          )}

          <ScheduleDialog
            isOpen={isScheduleDialogOpenB}
            onClose={handleScheduleDialogCloseB}
            applicationUUID={appB?.id}
            applicationId={appB?.application_id || ""}
            jobId={appB?.job?.id || ""}
            sessionType={sessionTypeB}
            isRescheduling={isReschedulingB}
          />
        </>
      )}
    </DashboardLayout>
  )
}

export default ApplicationCompare
